import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Warning, Wallet } from "@phosphor-icons/react";

const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Budget breach warning for an amount about to be approved. Staff see every live budget (invoiced + job costs) and, on a
 * breach, must tick the acknowledgement before approving; clients (portal=true) see invoiced-only budgets as a warning.
 */
export default function BudgetBreachBanner({ clientId, propertyId, amount, portal = false, acknowledged, onAcknowledge, onResult, className = "", testid = "budget-breach-banner" }) {
  const [check, setCheck] = useState(null);
  const amt = Number(amount || 0);
  useEffect(() => {
    if (!amt || (!portal && !clientId)) { setCheck(null); onResult && onResult(null); return; }
    let alive = true;
    API.get(portal ? "/client-hub/budgets/check" : "/budgets/check", { params: { amount: amt, ...(portal ? {} : { client_id: clientId }), ...(propertyId ? { property_id: propertyId } : {}) } })
      .then((r) => { if (!alive) return; setCheck(r.data); onResult && onResult(r.data); })
      .catch(() => { if (alive) { setCheck(null); onResult && onResult(null); } });
    return () => { alive = false; };
  }, [clientId, propertyId, amt, portal]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!check || !check.budgets?.length) return null;
  const tone = check.breach ? "border-red-500/60 bg-red-500/10" : check.warning ? "border-amber-500/60 bg-amber-500/10" : "border-border bg-card";
  const text = check.breach ? "text-red-700 dark:text-red-300" : check.warning ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground";
  return (
    <div className={`rounded-lg border p-3 text-sm ${tone} ${className}`} data-testid={testid}>
      <div className="flex items-start gap-2">
        {check.breach || check.warning ? <Warning size={18} weight="fill" className={`shrink-0 mt-0.5 ${text}`} /> : <Wallet size={18} className="shrink-0 mt-0.5 text-muted-foreground" />}
        <div className="min-w-0 flex-1">
          <p className={`font-semibold ${text}`} data-testid={`${testid}-message`}>{check.breach ? "Over budget" : check.warning ? "Near the budget limit" : "Within budget"} — {check.message}</p>
          <div className="mt-1.5 space-y-1">
            {check.budgets.map((b) => (
              <div key={b.id} className="text-xs" data-testid={`${testid}-row-${b.id}`}>
                <div className="flex justify-between gap-2"><span className="truncate">{portal ? b.name : <Link to={`/budgets?budget=${b.id}`} className="hover:underline">{b.name}</Link>}{b.property_name ? ` · ${b.property_name}` : ""}</span><span className={b.breach ? "text-red-600 font-medium" : b.state_after === "warning" ? "text-amber-600" : "text-muted-foreground"}>{gbp(b.spent)} → {gbp(b.after)} of {gbp(b.amount)} ({b.after_pct}%)</span></div>
                <div className="relative h-1.5 rounded-full bg-muted overflow-hidden mt-0.5"><div className={`absolute inset-y-0 left-0 ${b.breach ? "bg-red-500" : b.state_after === "warning" ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, b.after_pct)}%` }} /><div className="absolute inset-y-0 bg-foreground/30" style={{ left: `${Math.min(100, b.pct)}%`, width: 2 }} /></div>
              </div>
            ))}
          </div>
          {check.breach && onAcknowledge && (
            <label className="mt-2 flex items-center gap-2 text-xs font-medium cursor-pointer" data-testid={`${testid}-ack`}>
              <input type="checkbox" checked={!!acknowledged} onChange={(e) => onAcknowledge(e.target.checked)} data-testid={`${testid}-ack-checkbox`} /> I understand this exceeds the client's budget and want to proceed
            </label>
          )}
          {check.breach && portal && <p className="mt-1.5 text-[11px] text-muted-foreground">You can still approve — this is just so you're not caught out at month end.</p>}
        </div>
      </div>
    </div>
  );
}
